import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { MvaFields } from "@shared/mvaTracker";

/** One row of GET /api/mvas (server/mva-routes.ts). Dates are ISO with Z; dueOn is YYYY-MM-DD. */
export interface MvaRow {
  id: string;
  companyId: string; companyName: string; category: string; stage: string | null; industry: string | null;
  assignedTo: string | null; assigneeName: string | null;
  assignedBy: string | null; assignedByName: string | null;
  status: string; dueOn: string | null; notes: string | null;
  assignedAt: string; deliveredAt: string | null; updatedAt: string;
  docUploadedAt: string | null;
}

export type MvaScope = "open" | "delivered";

async function send<T>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Request failed (${r.status})`);
  return r.status === 204 ? (undefined as T) : r.json();
}

/**
 * The tracker is shared by the whole team, so unlike most queries here it
 * refetches on focus: coming back to the tab shows what Jackson changed.
 */
export function useMvas(scope: MvaScope) {
  return useQuery<MvaRow[]>({
    queryKey: ["/api/mvas", scope],
    queryFn: () => send<MvaRow[]>("GET", `/api/mvas?scope=${scope}`),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useMvaMutations() {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["/api/mvas"] });
    // The Prospects list carries each row's open MVA, and delivery can move the stage.
    qc.invalidateQueries({ queryKey: ["/api/prospects"] });
  };
  const onError = (e: Error) => toast.error(e.message);

  const start = useMutation({
    mutationFn: (input: MvaFields & { companyId: string }) => send<MvaRow>("POST", "/api/mvas", input),
    onSuccess: refresh,
    onError,
  });
  const update = useMutation({
    mutationFn: ({ id, ...fields }: MvaFields & { id: string }) => send<MvaRow>("PATCH", `/api/mvas/${id}`, fields),
    onSuccess: refresh,
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => send<void>("DELETE", `/api/mvas/${id}`),
    onSuccess: refresh,
    onError,
  });
  return { start, update, remove };
}
